import { RouteTypes } from 'consts';
import Header from 'containers/Header';
import Table from 'containers/Table';
import { useList } from 'hooks/list';
import { useEffect } from 'react';
import { useState } from 'react';
import { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { RfUiBreadcrumbs } from 'renfe-components';
import { createStructuredSelector } from 'reselect';
import { setLoading } from 'store/app/actions';
import {
  getAlumnosCurso,
  getAlumnosCursos,
  getAlumnosCursosCount,
  getExcelAlumnosCursos,
} from 'store/cursos/actions';
import {
  makeSelectAlumnosCurso,
  makeSelectAlumnosCursos,
  makeSelectAlumnosCursosCount,
} from 'store/cursos/selectors';
import { makeSelectTokenAccessToken } from 'store/oauth/selectors';
import { makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected } from 'store/usuarios/selectors';
import { getCellClassName } from 'utils/dateTimeUtils';
import { hasAccess } from 'utils/listUtils';
import { parseDate } from 'utils/parsePractices';
import { userProfile } from 'utils/profilesIndex';

const selectors = createStructuredSelector({
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  access_token: makeSelectTokenAccessToken(),
  alumnos: makeSelectAlumnosCursos(),
  alumnosCount: makeSelectAlumnosCursosCount(),
  alumnosCurso: makeSelectAlumnosCurso(),
} as any);

const backToCourses = {
  label: 'Listado de cursos',
  link: RouteTypes.COURSE,
  textHelp: 'Listado de cursos',
  active: false,
};

const backToPractice = {
  label: 'Listado de prácticas',
  link: RouteTypes.HOME,
  textHelp: 'Listado de prácticas',
  active: false,
};

const options =
  '[{"label": "Fecha inicio de mayor a menor","value": {"order":"FECHA_INI_ALUM_CURSO","ascDesc": "desc"},"active": "true"},{"label": "Fecha inicio de menor a mayor","value": {"order":"FECHA_INI_ALUM_CURSO","ascDesc": "asc"},"active": "false"},{"label": "Fecha fin de mayor a menor","value": {"order":"FECHA_FIN_ALUM_CURSO","ascDesc": "desc"},"active": "false"},{"label": "Fecha fin de menor a mayor","value": {"order":"FECHA_FIN_ALUM_CURSO","ascDesc": "asc"},"active": "false"}]';

const buildLinkIdAlumno = (
  nombreCurso: any,
  canSeeCourseAlumns: boolean,
  item: any
): string => {
  if (!canSeeCourseAlumns) {
    return '';
  }

  if (nombreCurso) {
    return RouteTypes.PRACT_ALU_CURSO.replace(
      ':cursoId?',
      item.ID_CURSO
    ).replace(':idAlumn?', item.ID_ALUMNO);
  }

  return RouteTypes.PRACTICES_ALUMN_COURSE.replace(
    ':cursoId?',
    item.ID_CURSO
  ).replace(':idAlumn?', item.ID_ALUMNO);
};

const formatAlumnoCursoDate = (date: any): string =>
  date !== null && date !== 0 ? parseDate(date) : 'Sin fecha';

const buildAlumnoParsed = (
  item: any,
  nombreCurso: any,
  canSeeCourseAlumns: boolean,
  profileActive: any
) => {
  const linkIdAlumno = buildLinkIdAlumno(
    nombreCurso,
    canSeeCourseAlumns,
    item
  );

  return {
    table: [
      [
        {
          text: item.ID_ALUMNO,
          urlLink: linkIdAlumno,
          className: getCellClassName(item, profileActive),
        },
        {
          text: item.NOMBRE_ALUMNO ? item.NOMBRE_ALUMNO : '',
        },
        {
          text: formatAlumnoCursoDate(item.FECHA_INI_ALUM_CURSO),
        },
        {
          text: formatAlumnoCursoDate(item.FECHA_FIN_ALUM_CURSO),
        },
        {
          text: item.nombre_perfil ? item.nombre_perfil : '',
        },
        {
          text: item.sociedad_parsed ? item.sociedad_parsed : '',
        },
      ],
    ],
  };
};

const AlumnosCurso = (): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { cursoId, nombreCurso, idAlumn }: any = useParams();
  const initFilter = {
    elemInicio: 0,
    elemFin: 10,
    orden: '',
    ascDesc: '',
    cursoId,
  };
  const { access_token, alumnos, profileActive, alumnosCount, alumnosCurso } =
    useSelector<any, any>(selectors);

  const [links] = useState([
    nombreCurso ? backToCourses : backToPractice,
    {
      label: 'Listado de alumnos por cursos',
      link: '#',
      textHelp: 'Listado de alumnos por cursos',
      active: true,
    },
  ]);

  const [curso, setCurso] = useState({
    id: cursoId,
    desc: nombreCurso,
    alumnId: idAlumn,
  });

  const { filterRef, data, setData, headers, handleFilterTable } = useList(
    { ...initFilter },
    [
      { text: 'Código Alumno' },
      { text: 'Nombre y Apellidos' },
      {
        text: 'Desde',
        orderBy: 'asc',
        ariaLabelIcon: 'Icono de ordenar',
      },
      {
        text: 'Hasta',
        orderBy: 'asc',
        ariaLabelIcon: 'Icono de ordenar',
      },
      { text: 'Perfil' },
      { text: 'Sociedad' },
    ],
    {
      action: (filter) => dispatch(getAlumnosCursos(filter)),
      headerToAPIMap: {
        Desde: 'FECHA_INI_ALUM_CURSO',
        Hasta: 'FECHA_FIN_ALUM_CURSO',
      },
    },
    () => null,
    async () => {
      dispatch(setLoading(true));
      await new Promise((resolve) => setTimeout(resolve, 90));
      dispatch(setLoading(false));
    }
  );

  const handleExportExcel = () => {
    dispatch(
      getExcelAlumnosCursos({
        filter: { cursoId: curso.id, ...filterRef.current },
        excelConfig: {
          literals: headers,
        },
      })
    );
  };

  const redirect = (e: any) => {
    history.push(e.detail);
  };

  useEffect(() => {
    if (profileActive && !curso.desc) {
      dispatch(getAlumnosCurso(curso.id));
    }
  }, [profileActive]);

  useEffect(() => {
    if (!alumnosCurso) {
      return;
    }

    setCurso({
      ...curso,
      desc: alumnosCurso.NOMBRE_CURSO,
    });
  }, [alumnosCurso]);

  useEffect(() => {
    const canSeeCourseAlumns = hasAccess(profileActive, [
      userProfile.gestor,
      userProfile.administrador,
      userProfile.formador,
      userProfile.supervisor,
      userProfile.visualizador,
    ]);

    const alumnosParsed = alumnos.map((item) =>
      buildAlumnoParsed(item, nombreCurso, canSeeCourseAlumns, profileActive)
    );

    setData(alumnosParsed);
  }, [alumnos]);

  useEffect(() => {
    if (profileActive) {
      dispatch(getAlumnosCursos({ cursoId: curso.id, ...filterRef.current }));
      dispatch(getAlumnosCursosCount({ cursoId: curso.id }));
    }
  }, [profileActive]);

  if (
    profileActive &&
    !(
      profileActive.ID_PERFIL === userProfile.formador ||
      profileActive.ID_PERFIL === userProfile.gestor ||
      profileActive.ID_PERFIL === userProfile.administrador ||
      profileActive.ID_PERFIL === userProfile.supervisor ||
      profileActive.ID_PERFIL === userProfile.visualizador
    )
  ) {
    return <Redirect to={RouteTypes.HOME} push />;
  }

  if (!access_token) {
    return <Redirect to={RouteTypes.LOGIN} push />;
  }

  return (
    <>
      <Header />
      <div className="rf-layout-table rf-practice-list">
        <RfUiBreadcrumbs
          links={JSON.stringify(links)}
          onEventChange={(e: any) => {
            history.push(e.detail);
          }}
        ></RfUiBreadcrumbs>
        <h1 className="rf-title rf-title--simple">
          Listado de alumnos - {curso.id} - {curso.desc}
        </h1>

        <Table
          redirect={(e: any) => {
            redirect(e);
          }}
          list={false}
          data={data}
          dataCount={alumnosCount}
          headers={headers}
          handleFilter={handleFilterTable}
          hideAudit={true}
          itemsOnLeft={2}
          handleExportExcel={handleExportExcel}
          orderMobile={true}
          orderOptions={options}
        />
      </div>
    </>
  );
};

export default AlumnosCurso;